import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @fileoverview MapSheetRow — LOCI event row for the map bottom sheet / rail.
 *
 * 64×64 square cover on the left, title + venue · time in the middle, mono
 * distance + price on the right. Active rows render with a teal wash
 * background that meets AA contrast against the body copy. Consumed by
 * `MapSheetContent` in the shared map discovery surface (FRE-760).
 */
import { forwardRef, useCallback } from 'react';
import { useTheme } from '../../../theme';
const COVER_SIZE = 64;
/**
 * `<MapSheetRow>` — compact horizontal event row for the map sheet/rail.
 *
 * Rendered as `role="button"` on a div so the row is keyboard-activatable via
 * Enter/Space while keeping the list semantics of the surrounding `<ul>`
 * intact. Active rows get a 10% opacity tint of the primary (teal) token plus
 * a 40% opacity border — the `color` stays at `theme.colors.text` (~18% oklch
 * in light mode) which keeps contrast well above AA on the tinted background.
 */
export const MapSheetRow = forwardRef(({ event, isActive, onSelect, className, style, 'data-testid': dataTestId = 'map-sheet-row' }, ref) => {
    const theme = useTheme();
    const { id, title, venue, time, price, distance, imageUrl } = event;
    const handleClick = useCallback(() => {
        onSelect(id);
    }, [id, onSelect]);
    const handleKeyDown = useCallback((e) => {
        if (e.target !== e.currentTarget)
            return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect(id);
        }
    }, [id, onSelect]);
    // Hex colors with explicit alpha so the "teal wash" renders identically
    // across browsers without relying on Tailwind primary/10 utilities that
    // this presentational component deliberately avoids.
    const rowStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing.md,
        width: '100%',
        padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
        background: isActive ? `${theme.colors.primary}1A` : 'transparent',
        border: `1px solid ${isActive ? `${theme.colors.primary}66` : 'transparent'}`,
        borderRadius: theme.borderRadius.md,
        color: theme.colors.text,
        textAlign: 'left',
        cursor: 'pointer',
        font: 'inherit',
        minHeight: 80,
    };
    const coverStyle = {
        width: COVER_SIZE,
        height: COVER_SIZE,
        flexShrink: 0,
        borderRadius: theme.borderRadius.sm,
        background: imageUrl
            ? `center / cover no-repeat url(${JSON.stringify(imageUrl)})`
            : theme.colors.surfaceAlt,
        backgroundColor: theme.colors.surfaceAlt,
    };
    const bodyStyle = {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    };
    const titleStyle = {
        margin: 0,
        fontFamily: theme.typography.fontFamilyDisplay ?? theme.typography.fontFamily,
        fontSize: theme.typography.fontSize.body,
        fontWeight: theme.typography.fontWeight.semibold,
        color: theme.colors.text,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        letterSpacing: '-0.005em',
    };
    const metaStyle = {
        margin: 0,
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.fontSize.small,
        color: theme.colors.textMuted,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    };
    const trailStyle = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        justifyContent: 'center',
        gap: 4,
        flexShrink: 0,
    };
    const priceStyle = {
        fontFamily: theme.typography.fontFamilyMono,
        fontSize: theme.typography.fontSize.small,
        color: theme.colors.text,
        fontFeatureSettings: "'tnum' 1, 'zero' 1",
    };
    const distanceStyle = {
        fontFamily: theme.typography.fontFamilyMono,
        fontSize: '12px',
        letterSpacing: '0.04em',
        color: theme.colors.textMuted,
        fontFeatureSettings: "'tnum' 1, 'zero' 1",
    };
    return (_jsxs("div", { ref: ref, role: "button", tabIndex: 0, onClick: handleClick, onKeyDown: handleKeyDown, "data-testid": dataTestId, "data-event-id": id, "data-active": isActive ? 'true' : 'false', "aria-pressed": isActive, "aria-label": `Open ${title} at ${venue}`, className: className, style: { ...rowStyle, ...style }, children: [_jsx("span", { "aria-hidden": "true", style: coverStyle, "data-testid": "map-sheet-row-cover" }), _jsxs("span", { style: bodyStyle, children: [_jsx("span", { style: titleStyle, children: title }), _jsxs("span", { style: metaStyle, "data-testid": "map-sheet-row-meta", children: [venue, " \u00B7 ", time] })] }), _jsxs("span", { style: trailStyle, children: [_jsx("span", { style: priceStyle, "data-testid": "map-sheet-row-price", children: price }), _jsx("span", { style: distanceStyle, "data-testid": "map-sheet-row-distance", children: distance })] })] }));
});
MapSheetRow.displayName = 'MapSheetRow';
